import type { JSX } from 'solid-js'
import { createMemo, createSignal, createUniqueId, mergeProps, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import FieldMessage from '~/components/FieldMessage'
import { useFieldError } from '~/hooks/useFieldError'
import type { CheckboxColorType, CheckboxPropsType, CheckboxSizeType } from '~/types/Checkbox'
import type { TooltipPlacementType } from '~/types/Tooltip'
import Tooltip from './Tooltip'

const sizeClasses: Record<CheckboxSizeType, { container: string; icon: string; label: string; spacing: string }> = {
  xs: { container: 'h-3 w-3', icon: 'h-2 w-2', label: 'text-xs', spacing: 'gap-1.5' },
  sm: { container: 'h-4 w-4', icon: 'h-3 w-3', label: 'text-sm', spacing: 'gap-2' },
  md: { container: 'h-5 w-5', icon: 'h-4 w-4', label: 'text-base', spacing: 'gap-2.5' },
  lg: { container: 'h-6 w-6', icon: 'h-5 w-5', label: 'text-lg', spacing: 'gap-3' },
  xl: { container: 'h-7 w-7', icon: 'h-6 w-6', label: 'text-xl', spacing: 'gap-3.5' }
}

const colorClasses: Record<CheckboxColorType, { base: string; hover: string; checked: string }> = {
  primary: {
    base: 'border-border-strong',
    hover: 'hover:border-primary',
    checked: 'bg-primary border-primary'
  },
  secondary: {
    base: 'border-border-strong',
    hover: 'hover:border-inverse',
    checked: 'bg-inverse border-inverse'
  },
  success: {
    base: 'border-border-strong',
    hover: 'hover:border-success',
    checked: 'bg-success border-success'
  },
  danger: {
    base: 'border-border-strong',
    hover: 'hover:border-danger',
    checked: 'bg-danger border-danger'
  },
  warning: {
    base: 'border-border-strong',
    hover: 'hover:border-warning',
    checked: 'bg-warning border-warning'
  },
  info: {
    base: 'border-border-strong',
    hover: 'hover:border-info',
    checked: 'bg-info border-info'
  },
  default: {
    base: 'border-border-strong',
    hover: 'hover:border-inverse',
    checked: 'bg-inverse border-inverse'
  }
}

export default function Checkbox(props: CheckboxPropsType): JSX.Element {
  const merged = mergeProps(
    {
      size: 'md' as CheckboxSizeType,
      color: 'primary' as CheckboxColorType,
      indeterminate: false,
      tooltipPlacement: 'top' as TooltipPlacementType
    },
    props
  )
  const [internalChecked, setInternalChecked] = createSignal(merged.checked ?? false)
  const uid = createUniqueId()

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto.
  const resolvedError = useFieldError(
    () => (merged.name ? String(merged.name) : undefined),
    () => merged.error
  )
  const hasError = () => Boolean(resolvedError())

  const sizeMemo = createMemo(() => sizeClasses[merged.size])
  const colorMemo = createMemo(() => colorClasses[merged.color])
  const isChecked = createMemo(() => {
    const checkedProp = merged.checked
    const checkedValue = typeof checkedProp === 'function' ? checkedProp() : (checkedProp ?? internalChecked())
    return !!checkedValue || merged.indeterminate
  })
  const handleChange = (event: Event & { currentTarget: HTMLInputElement; target: HTMLInputElement }) => {
    const isChecked = event.target.checked
    setInternalChecked(isChecked)
    if (typeof merged.onChange === 'function') merged.onChange(event)
  }

  const containerClasses = createMemo(() => {
    const size = sizeMemo()
    const color = colorMemo()
    const checked = isChecked()

    return twMerge(
      'relative inline-flex shrink-0 items-center justify-center z-20 rounded transition-colors duration-100',
      size.container,
      !checked ? 'border-2' : '',
      !checked && hasError() ? 'border-danger' : !checked ? color.base : '',
      !hasError() && !checked ? color.hover : '',
      checked ? color.checked : 'bg-surface',
      merged.class || ''
    )
  })

  const labelClasses = createMemo(() => twMerge('inline-flex items-center', sizeMemo().spacing))

  const spanClasses = createMemo(() => twMerge(sizeClasses[merged.size].label, 'text-foreground'))

  const renderCheckbox = () => (
    <div class="inline-flex flex-col">
      <label for={uid} class={labelClasses()}>
        <div class={containerClasses()} classList={merged.classList}>
          {/* pasar sólo props válidos al input para evitar atributos DOM no válidos */}
          {(() => {
            const rawChecked = merged.checked
            const resolvedChecked: boolean = (typeof rawChecked === 'function' ? rawChecked() : typeof rawChecked === 'boolean' ? rawChecked : internalChecked()) as boolean
            const {
              class: _c,
              classList: _cl,
              label: _label,
              helperText: _helperText,
              tooltip: _tooltip,
              tooltipPlacement: _tp,
              color: _color,
              indeterminate: _indeterminate,
              error: _error,
              checked: _checked2,
              id: _id,
              ...inputProps
            } = merged
            return (
              <input
                id={uid}
                {...inputProps}
                type="checkbox"
                class="absolute h-full w-full cursor-pointer opacity-0"
                checked={resolvedChecked}
                onChange={handleChange}
                aria-describedby={`${uid}-message`}
                aria-invalid={hasError() ? 'true' : undefined}
              />
            )
          })()}
          <Show when={isChecked()}>
            {merged.indeterminate ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                class="w-3/5 h-3/5 text-white"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="w-3/5 h-3/5 text-white"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </Show>
          <Show when={merged.indeterminate}>
            <span class="sr-only">Indeterminate</span>
          </Show>
        </div>
        <Show when={merged.label}>
          <span class={spanClasses()}>{merged.label}</span>
        </Show>
      </label>
      {/* Antes el mensaje se perdía: el error se convertía a booleano y sólo
          quedaba el borde en rojo, así que un campo obligatorio no decía por qué. */}
      <FieldMessage id={`${uid}-message`} size={merged.size} indent={false} reserve={false} error={resolvedError()} helperText={merged.helperText} />
    </div>
  )

  return (
    <Show when={merged.tooltip} fallback={renderCheckbox()}>
      <Tooltip content={merged.tooltip!} placement={merged.tooltipPlacement}>
        {renderCheckbox()}
      </Tooltip>
    </Show>
  )
}
